import { SupportedCollaterals } from "@astrid-dao/lib-base";
import { ethers, Contract } from "ethers";

import { BaseContractNames, TokenClaimingContracts, PublicSaleContracts } from "../config";
import { ContractsConfigJson } from "../providers/AstridDaoProvider";
import ActivePoolAbi from "./abi/astar/ActivePool.json";
import ATIDStakingAbi from "./abi/astar/ATIDStaking.json";
import ATIDTokenAbi from "./abi/astar/ATIDToken.json";
import BAITokenAbi from "./abi/astar/BAIToken.json";
import CommunityIssuanceAbi from "./abi/astar/CommunityIssuance.json";
import CrowdsaleAbi from "./abi/astar/Crowdsale.json";
import CrowdsaleRecordsAbi from "./abi/astar/CrowdsaleRecords.json";
import CrowdsaleWhitelistAbi from "./abi/astar/CrowdsaleWhitelist.json";
import DefaultPoolAbi from "./abi/astar/DefaultPool.json";
import GovTokenAbi from "./abi/astar/GovToken.json";
import PriceFeedAbi from "./abi/astar/PriceFeed.json";
import StabilityPoolAbi from "./abi/astar/StabilityPool.json";
import TokenAirdropAbi from "./abi/astar/TokenAirdrop.json";
import TokenLockupAbi from "./abi/astar/TokenLockup.json";
import VaultManagerAbi from "./abi/astar/VaultManager.json";
import WASTRAbi from "./abi/astar/WASTR.json";
import XcBaiWrapperAbi from "./abi/astar/XcBaiWrapper.json";
import ERC20Abi from "./abi/ERC20.json";
import ERC20PlusAbi from "./abi/ERC20Plus.json";
import ActivePoolTesterAbi from "./abi/shibuya/ActivePoolTester.json";
import ATIDStakingTesterAbi from "./abi/shibuya/ATIDStakingTester.json";
import ATIDTokenTesterAbi from "./abi/shibuya/ATIDTokenTester.json";
import BAITokenTesterAbi from "./abi/shibuya/BAITokenTester.json";
import CommunityIssuanceTesterAbi from "./abi/shibuya/CommunityIssuanceTester.json";
import CrowdsaleRecordsTesterAbi from "./abi/shibuya/CrowdsaleRecordsTester.json";
import CrowdsaleTesterAbi from "./abi/shibuya/CrowdsaleTester.json";
import CrowdsaleWhitelistTesterAbi from "./abi/shibuya/CrowdsaleWhitelistTester.json";
import DefaultPoolTesterAbi from "./abi/shibuya/DefaultPoolTester.json";
import GovTokenTesterAbi from "./abi/shibuya/GovTokenTester.json";
import PriceFeedTestnetAbi from "./abi/shibuya/PriceFeedTester.json";
import StabilityPoolTesterAbi from "./abi/shibuya/StabilityPoolTester.json";
import TokenLockupTesterAbi from "./abi/shibuya/TokenLockupTester.json";
import VaultManagerTesterAbi from "./abi/shibuya/VaultManagerTester.json";
import WASTRTesterAbi from "./abi/shibuya/WASTRTester.json";

type GetAbiParams = { isTestnet: boolean };

export const getAbi = ({ isTestnet }: GetAbiParams) => ({
  tokenLockup: isTestnet ? TokenLockupTesterAbi : TokenLockupAbi,
  tokenAirdrop: isTestnet ? TokenAirdropAbi : TokenAirdropAbi,
  astarPriceFeed: isTestnet ? PriceFeedTestnetAbi : PriceFeedAbi,
  activePool: isTestnet ? ActivePoolTesterAbi : ActivePoolAbi,
  stabilityPool: isTestnet ? StabilityPoolTesterAbi : StabilityPoolAbi,
  defaultPool: isTestnet ? DefaultPoolTesterAbi : DefaultPoolAbi,
  vaultManager: isTestnet ? VaultManagerTesterAbi : VaultManagerAbi,
  baiToken: isTestnet ? BAITokenTesterAbi : BAITokenAbi,
  govToken: isTestnet ? GovTokenTesterAbi : GovTokenAbi,
  atidToken: isTestnet ? ATIDTokenTesterAbi : ATIDTokenAbi,
  WASTR: isTestnet ? WASTRTesterAbi : WASTRAbi,
  ERC20: ERC20Abi,
  atidStaking: isTestnet ? ATIDStakingTesterAbi : ATIDStakingAbi,
  communityIssuance: isTestnet ? CommunityIssuanceTesterAbi : CommunityIssuanceAbi,
  crowdsale: isTestnet ? CrowdsaleTesterAbi : CrowdsaleAbi,
  crowdsaleRecords: isTestnet ? CrowdsaleRecordsTesterAbi : CrowdsaleRecordsAbi,
  crowdsaleWhitelist: isTestnet ? CrowdsaleWhitelistTesterAbi : CrowdsaleWhitelistAbi
});

export const currencyPriceMap = new Map<SupportedCollaterals, Contract>();
export const baseContractsMap = new Map<BaseContractNames, Contract>();
export const vaultManagerMap = new Map<SupportedCollaterals, Contract>();
export const collateralTokenMap = new Map<SupportedCollaterals, Contract>();
export const allCollateralTokenMap = new Map<string, Contract>();
export const activePoolMap = new Map<SupportedCollaterals, Contract>();
export const stabilityPoolMap = new Map<SupportedCollaterals, Contract>();
export const atidStakingMap = new Map<SupportedCollaterals, Contract>();
export const defaultPoolMap = new Map<SupportedCollaterals, Contract>();
export const publicSaleContractsMap = new Map<PublicSaleContracts, Contract>();
export const tokenClaimingContracts = new Map<TokenClaimingContracts, Contract>();

export const connectToContracts = (
  isTestnet: boolean,
  contractsConfig: ContractsConfigJson,
  web3Provider: any
) => {
  const abiMap = getAbi({
    isTestnet
  });

  const { baseContracts, airdropContracts, collaterals, publicSaleContracts } = contractsConfig;

  allCollateralTokenMap.set(
    "BAI",
    new Contract(baseContracts.baiToken, abiMap.baiToken, web3Provider)
  );
  allCollateralTokenMap.set(
    "ATID",
    new Contract(baseContracts.govToken, abiMap.govToken, web3Provider)
  );
  allCollateralTokenMap.set("WASTR", new Contract(baseContracts.WASTR, abiMap.WASTR, web3Provider));

  collaterals.forEach(collateralContracts => {
    const { name, contracts, address } = collateralContracts;
    const collateralName = name as SupportedCollaterals;

    const abiToUseForCollateralTokenContract =
      collateralName === "WASTR" ? abiMap.WASTR : abiMap.ERC20;

    collateralTokenMap.set(
      collateralName,
      new Contract(address, abiToUseForCollateralTokenContract, web3Provider)
    );
    allCollateralTokenMap.set(
      collateralName,
      new Contract(address, abiToUseForCollateralTokenContract, web3Provider)
    );

    currencyPriceMap.set(
      collateralName,
      new Contract(
        contracts.priceFeed,
        new ethers.utils.Interface(abiMap.astarPriceFeed),
        web3Provider
      )
    );

    vaultManagerMap.set(
      collateralName,
      new Contract(contracts.vaultManager, abiMap.vaultManager, web3Provider)
    );

    activePoolMap.set(
      collateralName,
      new Contract(contracts.activePool, abiMap.activePool, web3Provider)
    );

    stabilityPoolMap.set(
      collateralName,
      new Contract(contracts.stabilityPool, abiMap.stabilityPool, web3Provider)
    );

    atidStakingMap.set(
      collateralName,
      new Contract(contracts.atidStaking, abiMap.atidStaking, web3Provider)
    );

    defaultPoolMap.set(
      collateralName,
      new Contract(contracts.defaultPool, abiMap.defaultPool, web3Provider)
    );
  });

  baseContractsMap.set("baiToken", new Contract(baseContracts.baiToken, BAITokenAbi, web3Provider));

  baseContractsMap.set(
    "xcBaiToken",
    new Contract(baseContracts.xcBaiToken, ERC20PlusAbi, web3Provider)
  );

  baseContractsMap.set(
    "xcBaiWrapper",
    new Contract(baseContracts.xcBaiWrapper, XcBaiWrapperAbi, web3Provider)
  );

  baseContractsMap.set("govToken", new Contract(baseContracts.govToken, GovTokenAbi, web3Provider));

  baseContractsMap.set(
    "atidToken",
    new Contract(baseContracts.atidToken, ATIDTokenAbi, web3Provider)
  );

  baseContractsMap.set(
    "communityIssuance",
    new Contract(baseContracts.communityIssuance, CommunityIssuanceAbi, web3Provider)
  );

  if (airdropContracts.length) {
    airdropContracts.forEach(airdropContract => {
      const { name, contract } = airdropContract;

      tokenClaimingContracts.set(
        name as TokenClaimingContracts,
        new Contract(contract, abiMap.tokenAirdrop, web3Provider)
      );
    });
  }

  publicSaleContractsMap.set(
    "crowdsale",
    new Contract(publicSaleContracts.crowdsale, abiMap.crowdsale, web3Provider)
  );
  publicSaleContractsMap.set(
    "crowdsaleRecords",
    new Contract(publicSaleContracts.crowdsaleRecords, abiMap.crowdsaleRecords, web3Provider)
  );
};

export function assertContractExist(contract: Contract | undefined): Contract {
  if (!contract) {
    throw new Error("Contract does not exist");
  }
  return contract;
}
